class FacetFiltersForm extends HTMLElement {
  constructor() {
    super();
    this.onActiveFilterClick = this.onActiveFilterClick.bind(this);

    this.debouncedOnSubmit = debounce((event) => {
      this.onSubmitHandler(event);
    }, 800);

    // Ticks and sort apply at once; typed prices wait for a pause.
    this.querySelector('form').addEventListener('input', (event) => {
      if (event.target.type === 'text') this.debouncedOnSubmit(event);
      else this.onSubmitHandler(event);
    });
  }

  static setListeners() {
    const onHistoryChange = (event) => {
      const searchParams = event.state ? event.state.searchParams : FacetFiltersForm.searchParamsInitial;
      if (searchParams === FacetFiltersForm.searchParamsPrev) return;
      FacetFiltersForm.renderPage(searchParams, null, false);
    };
    window.addEventListener('popstate', onHistoryChange);

    // The panel is a native modal <dialog>: it traps focus, closes on Esc and
    // hands focus back to the trigger. A click on the dialog itself (not its
    // form, which fills it) is a click on the overlay.
    document.addEventListener('click', (event) => {
      const panel = document.getElementById('FacetsPanel');
      if (!panel) return;
      if (event.target.closest('[data-facets-open]')) panel.showModal();
      else if (event.target === panel || event.target.closest('[data-facets-close]')) panel.close();
    });
  }

  static renderPage(searchParams, event, updateURLHash = true) {
    FacetFiltersForm.searchParamsPrev = searchParams;
    const grid = document.getElementById('ProductGridContainer').querySelector('.collection');
    grid.classList.add('loading');
    grid.setAttribute('aria-busy', 'true');

    FacetFiltersForm.getSections().forEach((section) => {
      const url = `${window.location.pathname}?section_id=${section.section}&${searchParams}`;
      const cached = FacetFiltersForm.filterData.find((element) => element.url === url);

      if (cached) {
        FacetFiltersForm.renderSection(cached.html, event);
        return;
      }
      fetch(url)
        .then((response) => (response.ok ? response.text() : ''))
        .catch(() => '')
        .then((html) => {
          // A failed render is neither cached nor drawn: the grid just stops
          // loading and the next change tries again.
          if (!html) {
            grid.classList.remove('loading');
            grid.removeAttribute('aria-busy');
            return;
          }
          FacetFiltersForm.filterData = [...FacetFiltersForm.filterData, { html, url }];
          // A slower, older response must not overwrite a newer choice.
          if (searchParams !== FacetFiltersForm.searchParamsPrev) return;
          FacetFiltersForm.renderSection(html, event);
        });
    });

    if (updateURLHash) FacetFiltersForm.updateURLHash(searchParams);
  }

  static renderSection(html, event) {
    const parsedHTML = new DOMParser().parseFromString(html, 'text/html');
    FacetFiltersForm.renderFilters(parsedHTML, event);
    FacetFiltersForm.renderProductGridContainer(parsedHTML);
    FacetFiltersForm.revealActiveTab();
    if (typeof initializeScrollAnimationTrigger === 'function') initializeScrollAnimationTrigger(html.innerHTML);
  }

  // On phones the tab row scrolls: keep the selected metal clear of the
  // pinned Filter button instead of hidden under its fade.
  static revealActiveTab() {
    document.querySelectorAll('.metal-chips').forEach((row) => {
      const active = row.querySelector('[aria-current="true"]');
      if (!active || row.scrollWidth <= row.clientWidth) return;
      const left = active.getBoundingClientRect().left - row.getBoundingClientRect().left + row.scrollLeft;
      row.scrollLeft = left - (row.clientWidth - active.offsetWidth) / 2;
    });
  }

  static renderProductGridContainer(parsedHTML) {
    const container = document.getElementById('ProductGridContainer');
    container.innerHTML = parsedHTML.getElementById('ProductGridContainer').innerHTML;
    container.querySelectorAll('.scroll-trigger').forEach((element) => {
      element.classList.add('scroll-trigger--cancel');
    });
  }

  // Tabs, counts, the trigger label and every panel section carry
  // data-facets-refresh and an id; each is swapped for the server's copy.
  // The section being typed into is left alone so the caret stays put.
  static renderFilters(parsedHTML, event) {
    const typing = event && event.target && event.target.type === 'text';
    const keep = typing ? event.target.closest('[data-facets-refresh]') : null;
    const focusedId = document.activeElement ? document.activeElement.id : '';

    document.querySelectorAll('[data-facets-refresh]').forEach((target) => {
      if (target === keep) return;
      const source = parsedHTML.getElementById(target.id);
      // ponytail: a filter the server drops is hidden, one it adds is not inserted (reload shows it).
      target.hidden = !source;
      if (source) target.innerHTML = source.innerHTML;
    });

    if (focusedId && !typing) {
      // Clear all turns into plain text once nothing is left to clear: focus
      // then stays inside the open panel, on its close button.
      const focusTarget =
        document.getElementById(focusedId) || document.querySelector('.facets-panel[open] .facets-panel__close');
      if (focusTarget && focusTarget !== document.activeElement) focusTarget.focus({ preventScroll: true });
    }
  }

  static updateURLHash(searchParams) {
    history.pushState({ searchParams }, '', `${window.location.pathname}${searchParams && '?'.concat(searchParams)}`);
  }

  static getSections() {
    return [
      {
        section: document.getElementById('product-grid').dataset.id,
      },
    ];
  }

  createSearchParams(form) {
    // Empty price fields stay out of the URL.
    const entries = Array.from(new FormData(form)).filter(([, value]) => value !== '');
    return new URLSearchParams(entries).toString();
  }

  onSubmitForm(searchParams, event) {
    FacetFiltersForm.renderPage(searchParams, event);
  }

  onSubmitHandler(event) {
    event.preventDefault();
    this.onSubmitForm(this.createSearchParams(this.querySelector('form')), event);
  }

  onActiveFilterClick(event) {
    event.preventDefault();
    const url =
      event.currentTarget.href.indexOf('?') == -1
        ? ''
        : event.currentTarget.href.slice(event.currentTarget.href.indexOf('?') + 1);
    FacetFiltersForm.renderPage(url);
  }
}

FacetFiltersForm.filterData = [];
FacetFiltersForm.searchParamsInitial = window.location.search.slice(1);
FacetFiltersForm.searchParamsPrev = window.location.search.slice(1);
customElements.define('facet-filters-form', FacetFiltersForm);
FacetFiltersForm.revealActiveTab();
FacetFiltersForm.setListeners();

class PriceRange extends HTMLElement {
  constructor() {
    super();
    this.querySelectorAll('input').forEach((element) => {
      element.addEventListener('change', this.onRangeChange.bind(this));
      element.addEventListener('keydown', this.onKeyDown.bind(this));
    });
    this.setMinAndMaxValues();
  }

  onRangeChange(event) {
    const input = event.currentTarget;
    const before = input.value;
    this.adjustToValidValues(input);
    this.setMinAndMaxValues();
    // A clamped value must reach the URL too, not only the field.
    if (input.value !== before) input.dispatchEvent(new Event('input', { bubbles: true }));
  }

  onKeyDown(event) {
    if (event.metaKey) return;

    const pattern = /[0-9]|\.|,|'| |Tab|Backspace|Enter|ArrowUp|ArrowDown|ArrowLeft|ArrowRight|Delete|Escape/;
    if (!event.key.match(pattern)) event.preventDefault();
  }

  setMinAndMaxValues() {
    const inputs = this.querySelectorAll('input');
    const minInput = inputs[0];
    const maxInput = inputs[1];
    if (maxInput.value) minInput.setAttribute('data-max', maxInput.value);
    if (minInput.value) maxInput.setAttribute('data-min', minInput.value);
    if (minInput.value === '') maxInput.setAttribute('data-min', 0);
    if (maxInput.value === '') minInput.setAttribute('data-max', maxInput.getAttribute('data-max'));
  }

  adjustToValidValues(input) {
    const value = Number(input.value);
    const min = Number(input.getAttribute('data-min'));
    const max = Number(input.getAttribute('data-max'));

    if (value < min) input.value = min;
    if (value > max) input.value = max;
  }
}

customElements.define('price-range', PriceRange);

class FacetRemove extends HTMLElement {
  constructor() {
    super();
    const facetLink = this.querySelector('a');
    facetLink.setAttribute('role', 'button');
    facetLink.addEventListener('click', this.closeFilter.bind(this));
    facetLink.addEventListener('keyup', (event) => {
      event.preventDefault();
      if (event.code.toUpperCase() === 'SPACE') this.closeFilter(event);
    });
  }

  closeFilter(event) {
    event.preventDefault();
    const form = this.closest('facet-filters-form') || document.querySelector('facet-filters-form');
    form.onActiveFilterClick(event);
  }
}

customElements.define('facet-remove', FacetRemove);
